import { supabase } from './supabase';
import { User, Session, AuthError, EmailOtpType } from '@supabase/supabase-js';
import i18n from '../../i18n';

const log = {
  warn: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.warn(...args);
  },
  error: (...args: unknown[]) => {
    if (import.meta.env.DEV) console.error(...args);
  },
};

// ========================================
// Period Start Cache (optimizacija - period se mijenja 1× mjesečno)
// ========================================
let cachedPeriodStart: string | null = null;
let cachedPeriodExpiry: number = 0;

/**
 * Get current billing period start with caching
 * Period changes only once per month, so we cache it for 1 hour
 */
const getCachedPeriodStart = async (): Promise<string> => {
  const now = Date.now();

  // Return cached value if still valid
  if (cachedPeriodStart && now < cachedPeriodExpiry) {
    return cachedPeriodStart;
  }

  // Fetch from database
  const { data, error } = await supabase.rpc('get_current_period_start');
  if (error) throw error;
  if (!data) throw new Error('Failed to get period start');

  // Cache for 1 hour
  cachedPeriodStart = data;
  cachedPeriodExpiry = now + 60 * 60 * 1000;

  return data;
};

/**
 * Clear period cache (call when user logs out or on period change)
 */
export const clearPeriodCache = (): void => {
  cachedPeriodStart = null;
  cachedPeriodExpiry = 0;
};

// ========================================
// Types & Interfaces
// ========================================

export interface SupabaseUser extends User {
  email?: string;
}

export interface AuthSession {
  user: SupabaseUser;
  session: Session;
}

export interface SignUpResult {
  user: SupabaseUser;
  session: Session | null;
  /** True when Supabase did not create a session, meaning email confirmation is required. */
  requiresConfirmation: boolean;
}

export interface SubscriptionInfo {
  plan_name: string;
  status: string;
  expires_at: string;
  max_uploads_per_month: number;
  max_processing_jobs_per_month: number;
  max_storage_gb: number;
  max_file_size_mb: number;
  can_use_training: boolean;
  max_training_runs_per_month: number;
  plan_type: 'monthly' | 'hourly';
  total_compute_hours: number | null;
  tier_slug: string | null;
}

export interface UsageInfo {
  uploads_count: number;
  processing_jobs_count: number;
  training_runs_count: number;
  storage_used_gb: number;
  period_start: string;
  period_end: string;
  total_compute_seconds: number;
  next_reset_date?: string;
}

// ========================================
// Authentication Functions
// ========================================

/**
 * Check if email already exists in auth.users
 * Returns true if email is already registered
 */
export const checkEmailExists = async (email: string): Promise<boolean> => {
  try {
    // Try to get user by email (admin function)
    const { data, error } = await supabase.rpc('check_email_exists', {
      p_email: email.toLowerCase().trim(),
    });

    if (error) {
      // If function doesn't exist, fallback to signup attempt detection
      log.warn('check_email_exists function not available:', error);
      return false;
    }

    return data || false;
  } catch (err) {
    log.error('Error checking email existence:', err);
    return false;
  }
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (
  email: string,
  password: string
): Promise<AuthSession> => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  if (!data.user || !data.session) {
    throw new Error('Authentication failed: No user or session returned');
  }

  return { user: data.user, session: data.session };
};

/**
 * Sign up with email and password
 * Automatically assigns Free plan via database trigger
 *
 * IMPORTANT: If email confirmation is enabled in Supabase Dashboard,
 * this will return a user but no session until email is confirmed.
 * For development, disable email confirmation in:
 * Authentication → Providers → Email → Turn off "Confirm email"
 */
export const signUpWithEmail = async (
  email: string,
  password: string,
  metadata?: Record<string, unknown>
): Promise<SignUpResult> => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { lang: i18n.language, ...(metadata || {}) },
      emailRedirectTo: `${window.location.origin}/auth/confirm`,
    },
  });

  if (error) {
    log.error('Supabase signUp error:', error);
    throw error;
  }

  if (!data.user) {
    throw new Error('Registration failed: No user returned');
  }

  // Supabase anti-enumeration: with "Confirm email" enabled, signing up with an
  // ALREADY-registered (confirmed) email returns HTTP 200, no error, and a user
  // object whose `identities` array is EMPTY (and no confirmation email is
  // sent). Without this check the caller treats it as a fresh signup and shows
  // a misleading "check your inbox" instead of "this email is already
  // registered". Throw with status 422 so formatAuthError maps it to
  // `email_already_registered`. (Trade-off: surfacing this re-enables email
  // enumeration on the register form — an accepted product decision for clarity.
  // An existing UNCONFIRMED email keeps its identities, so it still re-sends the
  // confirmation rather than erroring.)
  if (Array.isArray(data.user.identities) && data.user.identities.length === 0) {
    throw Object.assign(new Error('User already registered'), { status: 422 });
  }

  return {
    user: data.user,
    session: data.session,
    requiresConfirmation: data.session === null,
  };
};

/**
 * Resend the signup confirmation email. Supabase enforces a 60s rate limit
 * per user — surface that error verbatim so callers can show a translated message.
 *
 * Note: lang is NOT passed here — the Supabase SDK's ResendParams.options only
 * accepts emailRedirectTo + captchaToken (no `data`). The language is tagged
 * once at signUp into user_metadata, and the Send Email Hook reads it from the
 * user record on every event (signup, recovery, magiclink, resend).
 */
export const resendConfirmationEmail = async (email: string): Promise<void> => {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email,
    options: {
      emailRedirectTo: `${window.location.origin}/auth/confirm`,
    },
  });
  if (error) {
    log.error('Supabase resend error:', error);
    throw error;
  }
};

/**
 * Exchange the PKCE `?code=` from the email confirmation link for a session.
 * On success, the Supabase client persists the session and `onAuthStateChange`
 * fires with event 'SIGNED_IN', which AuthContext already subscribes to.
 */
export const exchangeAuthCode = async (code: string): Promise<Session> => {
  const { data, error } = await supabase.auth.exchangeCodeForSession(code);
  if (error) {
    log.error('Supabase exchangeCodeForSession error:', error);
    throw error;
  }
  if (!data.session) {
    throw new Error('Code exchange returned no session');
  }
  return data.session;
};

/**
 * Verify a `token_hash` email link (signup confirm / recovery / magic link).
 * Used by AuthConfirmPage when the email links via `?token_hash=&type=` — the
 * format produced by the custom Send Email Hook — instead of the PKCE `?code=`.
 * Returns the established session (Supabase persists it + fires onAuthStateChange).
 */
export const verifyEmailOtp = async (
  tokenHash: string,
  type: EmailOtpType
): Promise<Session> => {
  const { data, error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
  if (error) {
    log.error('Supabase verifyOtp error:', error);
    throw error;
  }
  if (!data.session) {
    throw new Error('OTP verification returned no session');
  }
  return data.session;
};

/**
 * Sign out current user
 */
export const signOut = async (): Promise<void> => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
};

/**
 * Get current session
 */
export const getSession = async (): Promise<Session | null> => {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
};

/**
 * Get current user
 */
export const getCurrentUser = async (): Promise<SupabaseUser | null> => {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data.user;
};

/**
 * Listen to auth state changes
 * @param callback - Receives user, session, and event type
 */
export const onAuthStateChange = (
  callback: (user: SupabaseUser | null, session: Session | null, event: string) => void
) => {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((event, session) => {
    callback(session?.user || null, session, event);
  });

  return subscription;
};

/**
 * Reset password via email
 */
export const resetPassword = async (email: string): Promise<void> => {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`,
  });
  if (error) throw error;
};

/**
 * Update password (requires current session)
 */
export const updatePassword = async (newPassword: string): Promise<void> => {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  if (error) throw error;
};

// ========================================
// Subscription Functions
// ========================================

/**
 * Get user's active subscription info
 */
export const getUserSubscription = async (
  userId?: string
): Promise<SubscriptionInfo | null> => {
  const uid = userId || (await getCurrentUser())?.id;
  if (!uid) throw new Error('No user ID provided');

  const { data, error } = await supabase
    .from('user_subscriptions')
    .select(
      `
      status,
      expires_at,
      subscription_plans (
        name,
        max_uploads_per_month,
        max_processing_jobs_per_month,
        max_storage_gb,
        max_file_size_mb,
        can_use_training,
        max_training_runs_per_month,
        plan_type,
        total_compute_hours,
        tier_slug
      )
    `
    )
    .eq('user_id', uid)
    .eq('status', 'active')
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle(); // Use maybeSingle() to avoid 406 error when no subscription found

  if (error) {
    log.error('Error fetching subscription:', error);
    throw error;
  }

  // No active subscription — caller (AuthContext) must route the user to /pricing.
  if (!data || !data.subscription_plans) {
    return null;
  }

  const plan = Array.isArray(data.subscription_plans)
    ? data.subscription_plans[0]
    : data.subscription_plans;

  return {
    plan_name: plan.name,
    status: data.status,
    expires_at: data.expires_at,
    max_uploads_per_month: plan.max_uploads_per_month,
    max_processing_jobs_per_month: plan.max_processing_jobs_per_month,
    max_storage_gb: plan.max_storage_gb,
    max_file_size_mb: plan.max_file_size_mb ?? 10, // Default to Free plan limit if not set
    can_use_training: plan.can_use_training,
    // Use the DB value directly. -1 means unlimited, 0 means training not
    // available, any other positive number is the per-month cap. Fall back
    // to the can_use_training flag only if the column is missing (old rows).
    max_training_runs_per_month:
      (plan as { max_training_runs_per_month?: number }).max_training_runs_per_month
      ?? (plan.can_use_training ? -1 : 0),
    plan_type: plan.plan_type || 'monthly',
    total_compute_hours: plan.total_compute_hours ?? null,
    tier_slug: (plan as { tier_slug?: string | null }).tier_slug ?? null,
  };
};

/**
 * Get user's current usage for this billing period
 */
export const getUserUsage = async (
  userId?: string
): Promise<UsageInfo | null> => {
  const uid = userId || (await getCurrentUser())?.id;
  if (!uid) throw new Error('No user ID provided');

  // Get current period start (cached)
  const periodStart = await getCachedPeriodStart();

  const { data, error } = await supabase
    .from('usage_tracking')
    .select('*')
    .eq('user_id', uid)
    .eq('period_start', periodStart)
    .maybeSingle();

  if (error) {
    throw error;
  }

  // Fetch lifetime compute seconds via RPC
  let totalComputeSeconds = 0;
  try {
    const { data: computeData, error: computeError } = await supabase.rpc(
      'get_total_compute_seconds',
      { p_user_id: uid }
    );
    if (!computeError && computeData !== null) {
      totalComputeSeconds = Number(computeData) || 0;
    }
  } catch {
    log.warn('Failed to fetch total compute seconds');
  }

  // Next anniversary reset date (single source of truth: SQL).
  let nextResetDate: string | null = null;
  try {
    const { data: resetData } = await supabase.rpc('get_next_period_reset');
    if (resetData) nextResetDate = String(resetData);
  } catch (e) {
    log.warn('get_next_period_reset failed', e);
  }

  // No usage record yet (maybeSingle returns null), return zeros
  if (!data) {
    return {
      uploads_count: 0,
      processing_jobs_count: 0,
      training_runs_count: 0,
      storage_used_gb: 0,
      period_start: periodStart,
      period_end: nextResetDate
        ? new Date(nextResetDate + 'T00:00:00Z').toISOString()
        : new Date(new Date(periodStart).getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      total_compute_seconds: totalComputeSeconds,
      next_reset_date: nextResetDate ?? undefined,
    };
  }

  return {
    uploads_count: data.uploads_count || 0,
    processing_jobs_count: data.processing_jobs_count || 0,
    training_runs_count: data.training_runs_count || 0,
    storage_used_gb: Number(data.storage_used_gb) || 0,
    period_start: data.period_start,
    period_end: data.period_end,
    total_compute_seconds: totalComputeSeconds,
    next_reset_date: nextResetDate ?? undefined,
  };
};

// Removed dead exports incrementUsage / updateStorageUsage / checkQuota: usage is
// written by the backend (single source of truth); these had zero callers and the
// underlying RPCs (increment_usage/update_storage_usage are service_role-only;
// check_quota was locked down) are not for the client.

/**
 * Get user's complete profile (auth + subscription + usage)
 */
export const getUserProfile = async (userId?: string) => {
  const uid = userId || (await getCurrentUser())?.id;
  if (!uid) throw new Error('No user ID provided');

  const [user, subscription, usage] = await Promise.all([
    getCurrentUser(),
    getUserSubscription(uid),
    getUserUsage(uid),
  ]);

  return {
    user,
    subscription,
    usage,
  };
};

// ========================================
// Utility Functions
// ========================================

/**
 * Check if user is authenticated
 */
export const isAuthenticated = async (): Promise<boolean> => {
  const session = await getSession();
  return !!session;
};

/**
 * Get auth token for API requests
 */
export const getAuthToken = async (): Promise<string | null> => {
  const session = await getSession();
  return session?.access_token || null;
};

/**
 * Resend Supabase signup confirmation email for an unconfirmed user.
 * Returns true on success, false on failure (caller can surface a toast).
 */
export const resendSignupConfirmation = async (email: string): Promise<boolean> => {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email.toLowerCase().trim(),
  });
  if (error) {
    log.warn('[resendSignupConfirmation] failed', error);
    return false;
  }
  return true;
};

/**
 * Stable error codes for auth errors. Consumers translate these via i18n,
 * keeping this module framework-agnostic.
 */
export type AuthErrorCode =
  | 'invalid_credentials'
  | 'email_not_confirmed'
  | 'email_already_registered'
  | 'rate_limit'
  | 'network'
  | 'generic';

/**
 * Map a Supabase AuthError (or generic Error) to a stable error code.
 * The caller is responsible for translating the code into a user-facing string.
 */
export const formatAuthError = (error: AuthError | Error): AuthErrorCode => {
  const message = (error.message || '').toLowerCase();

  if ('status' in error) {
    const status = (error as AuthError).status;
    if (status === 400) {
      if (message.includes('email not confirmed')) return 'email_not_confirmed';
      return 'invalid_credentials';
    }
    if (status === 422) return 'email_already_registered';
    if (status === 429) return 'rate_limit';
  }

  if (message.includes('failed to fetch') || message.includes('networkerror')) {
    return 'network';
  }

  return 'generic';
};

// Export types for external use
export type { User, Session, AuthError };
